import { createContext, useState, ReactNode } from 'react';
import DefaultLanguage from '../Branding/DefaultLanguage.json';
import { Localization } from '@branding/config';
import { errorHandler } from '@/Config/ErrorHandler';
import { useQueryLog } from '@hooks/useQueryLog';

export const LocalizationContext = createContext({
	...DefaultLanguage,
	setLanguage: (language: string) => {},
	languageChanged: false,
	setLanguageChanged: (changed: boolean) => {},
	languageCode: DefaultLanguage.LanguageCode,
});

export function LocalizationProvider({ children }: { children: ReactNode }) {
	const { SupportedLanguages } = Localization;
	const [localLanguage, setLocalLanguage] = useState<typeof DefaultLanguage>(DefaultLanguage);
	const [languageChanged, setLanguageChanged] = useState<boolean>(false);
	const { sendLogToBackend } = useQueryLog();
	//We are using languageCode only for downtime screen. We need to know a language code when BE is down.
	const [languageCode, setLanguageCode] = useState<string>(DefaultLanguage.LanguageCode);

	function setLanguage(language: string) {
		setLanguageCode(language);
		if (SupportedLanguages?.length && !SupportedLanguages.includes(language)) {
			if (language === 'en') {
				language = 'en-US';
			} else {
				sendLogToBackend(`Language not supported: ${language}`, 'WARN');
				language = DefaultLanguage.LanguageCode;
			}
		}

		fetch(`/lang/${language}.json`)
			.then((res) => {
				if (res.status === 200 && res.ok) {
					return res.json();
				}
				throw new Error('Could not download language file ' + language + '.json');
			})
			.then((resData) => {
				setLocalLanguage(resData);
			})
			.catch((error) => {
				errorHandler(error, { msg: 'Error in LocalizationContext', error: error });
				setLocalLanguage(DefaultLanguage);
			})
			.finally(() => {
				if (language !== localLanguage.LanguageCode) {
					setLanguageChanged(true);
				}
			});
	}

	return (
		<LocalizationContext.Provider
			value={{
				...localLanguage,
				setLanguage,
				languageChanged,
				setLanguageChanged,
				languageCode,
			}}>
			{children}
		</LocalizationContext.Provider>
	);
}
